import React, { useState, useEffect } from "react";
import { ShoppingCart } from 'lucide-react';
import { getDatabase, ref, get, push, set } from "firebase/database";
import app from "../../firebase";
import Header from '../../Components/Architecture/ArchitectureHomeHeader';
import Footer from '../../Components/Mart/Footer/MartFooter';
import CartSidebar from "../../Components/Mart/Cart";
import { toast } from 'react-toastify';

const db = getDatabase(app);

const ArchitectureHomePage = () => {
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [addingToCart, setAddingToCart] = useState(null); // Track product ID instead of boolean

  const fetchAllProducts = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const architectsRef = ref(db, 'Architects');
      const architectsSnapshot = await get(architectsRef);
      
      if (!architectsSnapshot.exists()) {
        setProducts([]);
        return;
      }
      
      const architectsData = architectsSnapshot.val();
      const allProducts = [];
      
      for (const stateName in architectsData) {
        const stateData = architectsData[stateName];
        if (!stateData || typeof stateData !== 'object') continue;
        
        for (const districtName in stateData) {
          const districtData = stateData[districtName];
          if (!districtData || typeof districtData !== 'object') continue;
          
          for (const architectId in districtData) {
            const architectData = districtData[architectId];
            
            if (architectData.Products) {
              const productsData = architectData.Products;
              
              for (const categoryName in productsData) {
                const categoryProducts = productsData[categoryName];
                
                for (const productId in categoryProducts) {
                  const product = categoryProducts[productId];
                  
                  allProducts.push({
                    id: productId,
                    state: stateName,
                    district: districtName,
                    architectId: `${stateName}/${districtName}/${architectId}`,
                    architectAddress: architectData.address || 'No address provided',
                    category: categoryName,
                    ...product,
                    productName: product.productName || 'Unnamed Product',
                    price: product.price || 0,
                    discountPrice: product.discountPrice || null,
                    material: product.material || 'Material not specified',
                    color: product.color || 'Not specified',
                    size: product.size || 'Not specified',
                    theme: product.theme || 'Not specified',
                    isAntique: product.isAntique || false,
                    isEcofriendly: product.isEcofriendly || false,
                    isHandmade: product.isHandmade || false,
                    quantity: product.quantity || 1,
                    sellerEmail: product.sellerEmail || '',
                    images: product.images || [],
                    createdAt: product.createdAt || { ".sv": "timestamp" }
                  });
                }
              }
            } 
          }
        }
      }
      
      setProducts(allProducts);
    } catch (err) {
      console.error("Error fetching products:", err);
      setError(err.message || "Failed to fetch products");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAllProducts();
  }, []);

  const formatCurrency = (amount) => {
    if (!amount) return '₹0';
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(amount).replace('₹', '₹ ');
  };

  const handleAddToCart = async (product) => {
  try {
    setAddingToCart(product.id); // Set the current product ID being added
    
    const userId = localStorage.getItem('customerId');
    if (!userId) {
      toast.error("Please login to add items to cart");
      return;
    }

    const cartItem = {
      productId: product.id,
      productName: product.productName,
      vendorId: product.architectId,
      vendorName: product.architectAddress,
      category: product.category,
      image: product.images?.[0] || null,
      price: product.discountPrice || product.price,
      mrp: product.price,
      quantity: 1,
      size: product.size,
      material: product.material,
      addedAt: Date.now(),
      vendorType: 'Architects',
    };

    const cartRef = ref(db, `Users/${userId}/Cart`);
    const newCartItemRef = push(cartRef);
    await set(newCartItemRef, cartItem);

    toast.success("Product added to cart successfully!");
    setIsCartOpen(true);
  } catch (error) {
    console.error("Error adding to cart:", error);
    toast.error("Failed to add product to cart");
  } finally {
    setAddingToCart(null); // Reset the loading state
  }
};

  return (
    <div className="min-h-screen bg-gradient-to-b from-amber-50 to-white">
      <Header toggleCart={() => setIsCartOpen(!isCartOpen)} />
      <CartSidebar isOpen={isCartOpen} onClose={() => setIsCartOpen(false)} />
      
      <main>
        <section className="py-12 sm:py-16">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-3">
                Architectural Products
              </h2>
              <p className="text-gray-600 max-w-2xl mx-auto">
                Browse our collection of architectural products and resources
              </p>
            </div>

            {loading && (
              <div className="flex justify-center items-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-500"></div>
              </div>
            )}

            {error && (
              <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg mb-6 text-center">
                <div className="flex items-center justify-center">
                  <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                  {error}
                </div>
                <button 
                  onClick={fetchAllProducts}
                  className="mt-2 text-amber-600 hover:text-amber-700 font-medium"
                >
                  Try Again
                </button>
              </div>
            )}

            {!loading && !error && (
              <>
                {products.length > 0 ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 md:gap-8">
                    {products.map((product) => {
                      const hasDiscount = product.discountPrice && product.discountPrice < product.price;
                      const displayPrice = hasDiscount ? product.discountPrice : product.price;
                      const discountPercentage = hasDiscount 
                        ? Math.round(((product.price - product.discountPrice) / product.price) * 100)
                        : 0;

                      return (
                        <div
                          key={`${product.architectId}-${product.id}`}
                          className="bg-white rounded-xl shadow-md hover:shadow-lg transition-all duration-300 overflow-hidden group border border-gray-100 hover:border-amber-200"
                        >
                          <div className="relative h-48 sm:h-56 overflow-hidden">
                            {product.images && product.images.length > 0 ? (
                              <img
                                src={product.images[0]}
                                alt={product.productName}
                                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                                loading="lazy"
                              />
                            ) : (
                              <div className="w-full h-full bg-gray-100 flex items-center justify-center">
                                <svg
                                  className="w-16 h-16 text-gray-400"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={1}
                                    d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                                  />
                                </svg>
                              </div>
                            )}
                            
                            {hasDiscount && (
                              <div className="absolute top-3 right-3 bg-gradient-to-r from-amber-500 to-amber-600 text-white px-3 py-1 rounded-full text-xs font-bold shadow-md">
                                {discountPercentage}% OFF
                              </div>
                            )}
                          </div>

                          <div className="p-5">
                            <h3 className="text-lg md:text-xl font-semibold text-gray-900 mb-2 line-clamp-2">
                              {product.productName}
                            </h3>

                            <p className="text-sm text-gray-600 mb-2 line-clamp-1">
                              {product.material} • {product.color}
                            </p>

                            <div className="flex items-center justify-between">
                              <div>
                                <span className="text-xl font-bold text-amber-700">
                                  {formatCurrency(displayPrice)}
                                </span>
                                {hasDiscount && (
                                  <span className="text-sm text-gray-500 line-through ml-2">
                                    {formatCurrency(product.price)}
                                  </span>
                                )}
                              </div>
                            </div>

                           <button 
                              className="mt-4 w-full bg-gradient-to-r from-amber-500 to-amber-600 text-white py-2 rounded-lg hover:from-amber-600 hover:to-amber-700 transition duration-300 shadow hover:shadow-md flex items-center justify-center"
                              onClick={() => handleAddToCart(product)}
                              disabled={addingToCart === product.id} // Only disable if this product is being added
                            >
                              <ShoppingCart size={18} className="mr-2" />
                              {addingToCart === product.id ? 'Adding...' : 'Add to Cart'}
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <svg
                      className="mx-auto h-12 w-12 text-gray-400 mb-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={1}
                        d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"
                      />
                    </svg>
                    <h3 className="text-lg font-medium text-gray-900 mb-2">
                      No products available
                    </h3>
                    <p className="text-gray-500 mb-4">
                      There are currently no architectural products listed.
                    </p>
                  </div>
                )}
              </>
            )}
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default ArchitectureHomePage;